import { AppConfirmation } from '@nxsys/components/AppConfirmationModal';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  useImportEmployee,
  useImportProspect,
  useImportTimesheet,
} from '@nxsys/hooks/api/import';
import useImportSocket from '@nxsys/hooks/useImportSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { importAction } from '@nxsys/store/slices/import/reducer/importSlice';
import { IRELAND } from '@nxsys/store/constants/keys';
import { GetAgencyUploadListParams } from '@nxsys/store/slices/import/types';
import { showMessage } from '@nxsys/utils/message';
import { debounce, toNumber } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CancelUploadedFileModal from '../components/CancelUploadedFileModal';
import ImportResultUploadModal from '../components/ImportResultUploadModal';
import ViewAgencyUploadFile from '../components/ViewAgencyUploadFile';
import {
  AGENCY_UPLOAD_FILE_TYPE,
  confirmCancelUploadFileLabel,
  importTypeByAgencyUploadedFile,
  viewUploadedFileByAgencyTitle,
} from '../constants';
import { getAgencyUploadColumns } from './columns';
import ImportTimesheetResultUploadModal from '../components/ImportTimesheetResultUploadModal';

const AgencyUploadTab = ({
  fileType,
}: {
  fileType: AGENCY_UPLOAD_FILE_TYPE;
}) => {
  const { messages } = useTypedIntl();

  const { user, isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const { agencyUploadList } = useSelector(
    (state: AppState) => state.importStore,
  );
  const { loading, list, pagination, queryParams } = agencyUploadList;

  const dispatch = useDispatch<AppDispatch>();

  const [searchValue, setSearchValue] = useState('');
  const [dataImported, setDataImported] = useState<
    IImportEmployee | IImportProspect | IImportTimesheet | null
  >(null);
  const [dataUploaded, setDataUploaded] = useState<IAgencyUpload | null>(null);
  const [openViewImportedFileModal, setOpenViewImportedFileModal] =
    useState(false);
  const [openResultUploadModal, setOpenResultUploadModal] = useState(false);
  const [openCancelUploadedFileModal, setOpenCancelUploadedFileModal] =
    useState(false);
  const [openResultUploadTimesheetModal, setOpenResultUploadTimesheetModal] =
    useState(false);

  const { run: importEmployee, loading: importEmployeeLoading } =
    useImportEmployee();

  const { run: importProspect, loading: importProspectLoading } =
    useImportProspect();

  const { run: importTimesheet, loading: importTimesheetLoading } =
    useImportTimesheet();

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetAgencyUploadListParams) => {
    const newParams = {
      ...params,
      fileType,
    };
    dispatch(importAction.getAgencyUploadList(newParams));
  };

  const refetchData = () =>
    handleFetchData({
      ...queryParams,
    });

  const refetchDataRef = useRef(refetchData);
  refetchDataRef.current = refetchData;

  useImportSocket({
    eventName:
      fileType === AGENCY_UPLOAD_FILE_TYPE.TIMESHEETS
        ? `import-timesheet-process-notification-${user?.id}`
        : `import-process-notification-${user?.id}`,
    onSuccess: useCallback(() => {
      showMessage({
        type: 'success',
        content: messages['import.timesheet.importSuccess'],
      });
      setTimeout(() => {
        refetchDataRef.current();
        dispatch(importAction.getAgencyUploadSummary());
      }, 1000);
    }, [messages]),
    onError: useCallback(
      (error: string) => {
        showMessage({
          type: 'error',
          content: error || 'Import failed',
        });
        setTimeout(() => {
          refetchDataRef.current();
        }, 1000);
      },
      [],
    ),
  });

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedFetchData(value);
  };

  // const handleViewUploadedFile = useCallback((data: IAgencyUpload) => {
  //   setDataUploaded(data);
  //   setOpenViewImportedFileModal(true);
  // }, []);

  const handleImport = useCallback(async (data: IAgencyUpload) => {
    if (!data) return;
    // The backend picks the column set from these flags, exactly as the
    // direct Employee/Prospect import tabs send them; without them an Irish
    // umbrella file is validated against the UK umbrella columns.
    if (fileType === AGENCY_UPLOAD_FILE_TYPE.SUBCONTRACTORS) {
      setOpenResultUploadModal(true);
      const res = await importEmployee({
        fileName: data?.fileName,
        fileSize: toNumber(data?.fileSize),
        url: data?.attachmentURL,
        submittedId: data?.id,
        isBureau,
        isIreland,
      });
      setDataImported(res.data);
    } else if (fileType === AGENCY_UPLOAD_FILE_TYPE.PROSPECTS) {
      setOpenResultUploadModal(true);
      const res = await importProspect({
        fileName: data?.fileName,
        fileSize: toNumber(data?.fileSize),
        url: data?.attachmentURL,
        submittedId: data?.id,
        isIreland,
        isBureau,
      });
      setDataImported(res.data);
    } else if (fileType === AGENCY_UPLOAD_FILE_TYPE.TIMESHEETS) {
      setOpenResultUploadTimesheetModal(true);
      const res = await importTimesheet({
        fileName: data?.fileName,
        fileSize: toNumber(data?.fileSize),
        url: data?.attachmentURL,
        submittedId: data?.id,
      });
      setDataImported(res.data);
    }
  }, [fileType, isBureau, isIreland]);

  const handleOpenCancelModal = useCallback(async (data: IAgencyUpload) => {
    if (!data) return;
    AppConfirmation.open({
      title: confirmCancelUploadFileLabel[fileType].title,
      paragraph: confirmCancelUploadFileLabel[fileType].content,
      onConfirm: () => {
        setOpenCancelUploadedFileModal(true);
        setDataUploaded(data);
      },
    });
  }, []);

  return (
    <>
      <div className="ImportProspectTab">
        <AppsHeader>
          <AppInputSearch value={searchValue} onChange={handleSearchChange} />
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            data={
              list?.map((item, index) => ({
                ...item,
                indexNumber:
                  pagination.current * pagination.pageSize + index + 1,
              })) ?? []
            }
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 2000, y: 700 }}
            columns={getAgencyUploadColumns({
              messages,
              // onView: handleViewUploadedFile,
              onImport: handleImport,
              onCancel: handleOpenCancelModal,
            })}
            onChangePagination={onChangePagination}
            noDataType={!!queryParams?.search ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
        <ViewAgencyUploadFile
          isOpen={openViewImportedFileModal}
          toggle={() =>
            setOpenViewImportedFileModal(!openViewImportedFileModal)
          }
          data={dataUploaded}
          title={viewUploadedFileByAgencyTitle[fileType]}
          onImport={() => dataUploaded && handleImport(dataUploaded)}
        />
        <ImportResultUploadModal
          isOpen={openResultUploadModal}
          toggle={() => setOpenResultUploadModal(!openResultUploadModal)}
          resultData={dataImported}
          loading={importEmployeeLoading || importProspectLoading}
          refetchData={refetchData}
          importScreenType={importTypeByAgencyUploadedFile[fileType]}
        />
        <ImportTimesheetResultUploadModal
          isOpen={openResultUploadTimesheetModal}
          toggle={() => {
            setOpenResultUploadTimesheetModal(!openResultUploadTimesheetModal);
            setDataImported(null);
          }}
          resultData={dataImported as IImportTimesheet}
          loading={importTimesheetLoading}
          refetchData={refetchData}
        />
        <CancelUploadedFileModal
          isOpen={openCancelUploadedFileModal}
          toggle={() =>
            setOpenCancelUploadedFileModal(!openCancelUploadedFileModal)
          }
          fileId={dataUploaded?.id}
          refetchData={refetchData}
        />
      </div>
    </>
  );
};

export default AgencyUploadTab;
